import {
    Breakpoints,
    Container,
    ContainerPadding,
    CTA,
    Layout,
    LayoutRowGap,
    Text,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import {Close} from '@volkswagen-onehub/icons-core'
import {globalColorsNBD} from '@volkswagen-onehub/tokens'
import styled from 'styled-components'

import React, {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {
    COUPON_REQUEST_FORM_ID,
    CouponRequestForm,
} from '../form/CouponRequestForm'
import {useSelector} from 'react-redux'
import {closeCouponLayer} from './launchers/CouponRequestLayerHandle'
import {RootState} from '../../rootReducer'
import {ContainerFooterCTA, FooterTextContainer} from '../../../shared/classes'
import setLocalTracking from '../../../utils/tracking/tracking-utils/setLocalTracking'
import TrackingAction from '../../../tracking-manager'
import {
    getBasicButtonClickData,
    getFormLayerCloseData,
    getFormLayerLoadData,
} from '../../../utils/tracking/getTrackingData'
import store from '../../store'
import {getTrackingModel} from '../../../utils/tracking/tracking-utils/getTrackingModel'
import {globalColors} from '../../../utils/style/globalColors'
import {hasLegalNotes} from '../../../utils/promos/hasLegalNotes'
import {FormPromotionSummary} from '../form/FormPromotionSummary'
import {FormPromotionLegalNotes} from '../form/FormPromotionLegalNotes'

const COUPON_LAYER_ID = 'coupon-request-layer'
const FORM_BORDER_COLOR = globalColorsNBD.digitalCobalt['15']

const CouponLayerHeader = styled.header`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: ${(props) => props.theme.size.static200};
    box-sizing: border-box;
    width: 100%;
    padding-top: ${(props) => props.theme.size.static250};
    padding-right: 5px;
    padding-bottom: ${(props) => props.theme.size.static250};
    border-bottom: ${(props) => props.theme.size.border.light} solid
        ${FORM_BORDER_COLOR};
    background-color: ${globalColors.white000};

    #${COUPON_LAYER_ID} > div > div > div:has(> &) {
        position: sticky;
        top: 0;
        background-color: ${globalColors.white000};
        width: 100%;
        z-index: 2;
    }
`

const CouponHeaderTitle = styled(Text)`
    min-width: 0;
    margin: 0;
    padding-left: ${(props) => props.theme.size.grid001};
    overflow-wrap: anywhere;

    @media (min-width: ${Breakpoints.b560}px) {
        padding-left: ${(props) => props.theme.size.static450};
    }

    @media (min-width: ${Breakpoints.b1600}px) {
        padding-left: ${(props) => props.theme.size.static550};
    }
`

const CouponHeaderCloseCTA = styled(CTA)`
    grid-column: 2;
    justify-self: end;
    flex: 0 0 auto;
`

const SubmitContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    box-sizing: border-box;
    width: 100%;
    padding-top: ${(props) => props.theme.size.static250};
    padding-right: ${(props) => props.theme.size.static250};
    padding-bottom: ${(props) => props.theme.size.static250};
    padding-left: ${(props) => props.theme.size.grid001};
    border-top: ${(props) => props.theme.size.border.light} solid
        ${FORM_BORDER_COLOR};
    background-color: ${(props) => props.theme.colors.background.primary};

    @media (min-width: ${Breakpoints.b560}px) {
        padding-left: ${(props) => props.theme.size.static450};
    }

    @media (min-width: ${Breakpoints.b1600}px) {
        padding-left: ${(props) => props.theme.size.static550};
    }

    #${COUPON_LAYER_ID} > div > div > div:has(> &) {
        position: sticky;
        bottom: 0;
        background-color: ${(props) => props.theme.colors.background.primary};
        width: 100%;
        z-index: 1;
    }
`

const COUPON_LAYER_TITLE_ID = 'coupon-request-layer-title'

// tslint:disable-next-line:cyclomatic-complexity
export function CouponRequestLayer(): React.JSX.Element {
    const {t} = useTranslation('app')
    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const model = useSelector((state: RootState) => state.general.singleCar)
    const selectedPromotion = useSelector(
        (state: RootState) => state.general.selectedPromotion
    )
    const thanksPage = useSelector(
        (state: RootState) => state.userData.thanksPage
    )
    const selectedFormModel = useSelector(
        (state: RootState) => state.userData.selectedFormModel
    )
    const formPromotion = useSelector(
        (state: RootState) => state.userData.formPromotion
    )
    const thanksTitleRef = useRef<HTMLElement>(null)

    const trackingLayerClose = () => {
        const trackingModel = getTrackingModel(model, selectedFormModel)

        setLocalTracking(
            TrackingAction.VWBASIC_FORM_LAYER_CLOSE_CLICK,
            getFormLayerCloseData(
                trackingModel?.name,
                trackingModel?.slug,
                trackingBrand,
                isShowroom,
                model && model.id,
                selectedPromotion?.title,
                selectedPromotion?.motorization
            )
        )
    }

    const trackingButtonClick = (linkName: string) => {
        const trackingModel = getTrackingModel(model, selectedFormModel)

        setLocalTracking(
            TrackingAction.VWBasic_Button_Click,
            getBasicButtonClickData(
                trackingModel?.name,
                trackingModel?.slug,
                trackingBrand,
                isShowroom,
                model && model.id,
                selectedPromotion?.title,
                selectedPromotion?.motorization,
                linkName
            )
        )
    }

    useEffect(() => {
        const trackingModel = getTrackingModel(model, selectedFormModel)

        setLocalTracking(
            TrackingAction.VWBASIC_FORM_LAYER_LOAD,
            getFormLayerLoadData(
                trackingModel?.name,
                trackingModel?.slug,
                trackingBrand,
                isShowroom,
                model && model.id,
                selectedPromotion?.title,
                selectedPromotion?.motorization,
                store.getState().userData.formCycleID
            )
        )
    }, [])

    useEffect(() => {
        if (thanksPage) {
            thanksTitleRef.current?.focus()
        }
    }, [thanksPage])

    const couponRequestTitle =
        model && model.slug
            ? t('coupon.couponRequest.titleWithModel', {
                  modelName: model.name,
              })
            : t('coupon.couponRequest.title')
    const layerTitle = thanksPage
        ? t('coupon.thank_you_page.title')
        : couponRequestTitle

    return (
        <div id={COUPON_LAYER_ID}>
            <Container wrap="always">
                <CouponLayerHeader>
                    <CouponHeaderTitle
                        id={COUPON_LAYER_TITLE_ID}
                        ref={thanksTitleRef}
                        tag={TextTag.h2}
                        bold
                        appearance={TokenTextAppearance.headline300}
                        role={thanksPage ? 'status' : undefined}
                        aria-live={thanksPage ? 'assertive' : undefined}
                        aria-atomic={thanksPage ? true : undefined}
                        tabIndex={-1}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: layerTitle,
                            }}
                        />
                    </CouponHeaderTitle>
                    <CouponHeaderCloseCTA
                        tag="button"
                        emphasis="tertiary"
                        aria-label={t('cta.closeLayerLabel')}
                        iconPosition="top"
                        onClick={() => {
                            trackingLayerClose()
                            closeCouponLayer()
                        }}
                        icon={<Close aria-hidden={true} />}
                    />
                </CouponLayerHeader>

                <Container
                    padding={{
                        left: {
                            [Breakpoints.default]: ContainerPadding.grid001,
                            [Breakpoints.b560]: ContainerPadding.static450,
                            [Breakpoints.b1600]: ContainerPadding.static550,
                        },
                        right: {
                            [Breakpoints.default]: ContainerPadding.grid001,
                            [Breakpoints.b560]: ContainerPadding.static450,
                            [Breakpoints.b1600]: ContainerPadding.static550,
                        },
                        top: ContainerPadding.static350,
                        bottom: ContainerPadding.static350,
                    }}
                >
                    {!thanksPage ? (
                        <>
                            <Layout rowGap={LayoutRowGap.static500}>
                                {formPromotion ? (
                                    <FormPromotionSummary
                                        promotion={formPromotion}
                                    />
                                ) : undefined}
                                <Text
                                    tag={TextTag.p}
                                    appearance={TokenTextAppearance.copy200}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: t(
                                                'coupon.couponRequest.description'
                                            ),
                                        }}
                                    />
                                </Text>
                                <Text
                                    tag={TextTag.p}
                                    appearance={TokenTextAppearance.copy100}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: t(
                                                'coupon.couponRequest.required'
                                            ),
                                        }}
                                    />
                                </Text>
                            </Layout>
                            <CouponRequestForm />
                            {formPromotion &&
                            hasLegalNotes(formPromotion.tooltip_text) ? (
                                <FormPromotionLegalNotes
                                    legalNotes={formPromotion.tooltip_text}
                                />
                            ) : undefined}
                        </>
                    ) : (
                        <>
                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy200}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'coupon.thank_you_page.subtitle'
                                        ),
                                    }}
                                />
                            </Text>

                            <FooterTextContainer>
                                <Text
                                    tag={TextTag.p}
                                    appearance={TokenTextAppearance.copy200}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: t(
                                                'coupon.thank_you_page.content'
                                            ),
                                        }}
                                    />
                                </Text>

                                <ContainerFooterCTA>
                                    <Layout rowGap={LayoutRowGap.static350}>
                                        <Container horizontalAlign="center">
                                            <CTA
                                                tag="a"
                                                emphasis="primary"
                                                href={
                                                    model.configurator_cta_url
                                                        ? model.configurator_cta_url
                                                        : t(
                                                              'coupon.thank_you_page.ctaLinkConfigure'
                                                          )
                                                }
                                                onClick={() => {
                                                    trackingButtonClick(
                                                        t(
                                                            'coupon.thank_you_page.ctaLabelConfigure'
                                                        )
                                                    )
                                                }}
                                            >
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: t(
                                                            'coupon.thank_you_page.ctaLabelConfigure'
                                                        ),
                                                    }}
                                                />
                                            </CTA>
                                        </Container>
                                        <Container horizontalAlign="center">
                                            <CTA
                                                tag="button"
                                                emphasis="secondary"
                                                onClick={() => {
                                                    trackingButtonClick(
                                                        t(
                                                            'coupon.thank_you_page.ctaLabelClose'
                                                        )
                                                    )
                                                    closeCouponLayer()
                                                }}
                                                title={t(
                                                    'coupon.thank_you_page.ctaLabelClose'
                                                )}
                                                aria-label={t(
                                                    'coupon.thank_you_page.ctaLabelClose'
                                                )}
                                            >
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: t(
                                                            'coupon.thank_you_page.ctaLabelClose'
                                                        ),
                                                    }}
                                                />
                                            </CTA>
                                        </Container>
                                    </Layout>
                                </ContainerFooterCTA>
                            </FooterTextContainer>
                        </>
                    )}
                </Container>

                {!thanksPage ? (
                    <SubmitContainer>
                        <CTA
                            tag="button"
                            emphasis="primary"
                            type="submit"
                            {...({form: COUPON_REQUEST_FORM_ID} as {
                                form: string
                            })}
                            title={t('coupon.couponRequest.cta_submit')}
                            aria-label={t('coupon.couponRequest.cta_submit')}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'coupon.couponRequest.cta_submit'
                                    ),
                                }}
                            />
                        </CTA>
                    </SubmitContainer>
                ) : undefined}
            </Container>
        </div>
    )
}
